import React, { useEffect, useMemo, useRef, useState, useCallback, Suspense } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Environment, Html } from "@react-three/drei";
import * as THREE from "three";
import { getNumberColor } from "../utils/gameLogic";
import { sounds } from "../utils/audio";
import "./Game.css";

// European Roulette sequence (37 pockets)
const EUROPEAN_WHEEL = [
  0, 32, 15, 19, 4, 21, 2, 25, 17, 34, 6, 27, 13, 36, 11, 30, 8, 23,
  10, 5, 24, 16, 33, 1, 20, 14, 31, 9, 22, 18, 29, 7, 28, 12, 35, 3, 26
];

const AMERICAN_WHEEL = [
  0, 28, 9, 26, 30, 11, 7, 20, 32, 17, 5, 22, 34, 15, 3, 24, 36, 13, 1,
  "00", 27, 10, 25, 29, 12, 8, 19, 31, 18, 6, 21, 33, 16, 4, 23, 35, 14, 2
];

const RED_NUMBERS = new Set([
  1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36
]);

const TAU = Math.PI * 2;

function getPocketColor(num) {
  if (num === 0 || num === "00") return "#008544"; // Emerald Green
  const n = Number(num);
  if (RED_NUMBERS.has(n)) return "#ff0000"; // Full Pure Red
  return "#000000"; // Full Pure Black
}

// Generate unified, photorealistic 2048x2048 texture for the entire numbered rotor ring (exact match to image)
function createPhotorealisticRotorTexture(sequence) {
  const canvas = document.createElement("canvas");
  canvas.width = 2048;
  canvas.height = 2048;
  const ctx = canvas.getContext("2d");

  const cx = 1024;
  const cy = 1024;
  const total = sequence.length;
  const step = TAU / total;

  const rOuter = 1024;
  const rInner = 490;
  const rText = 940;

  ctx.clearRect(0, 0, 2048, 2048);

  // 1. Draw each pocket slice seamlessly (single continuous pocket with its true color)
  for (let i = 0; i < total; i++) {
    const num = sequence[i];
    const startAngle = i * step - step / 2;
    const endAngle = i * step + step / 2;
    const midAngle = i * step;

    // A. Entire Pocket Surface (Crimson Red / Jet Charcoal / Emerald Green)
    // Full continuous strip from rInner to rOuter with NO middle band or dividing line
    ctx.beginPath();
    ctx.arc(cx, cy, rOuter, startAngle, endAngle, false);
    ctx.arc(cx, cy, rInner, endAngle, startAngle, true);
    ctx.closePath();
    ctx.fillStyle = getPocketColor(num);
    ctx.fill();

    // B. Gold Fret Divider Line between pockets
    ctx.beginPath();
    ctx.moveTo(cx + Math.cos(startAngle) * rInner, cy + Math.sin(startAngle) * rInner);
    ctx.lineTo(cx + Math.cos(startAngle) * rOuter, cy + Math.sin(startAngle) * rOuter);
    ctx.strokeStyle = "#ffc107";
    ctx.lineWidth = 4;
    ctx.stroke();

    // C. Number Typography - Placed directly along the outer edge
    ctx.save();
    const tx = cx + Math.cos(midAngle) * rText;
    const ty = cy + Math.sin(midAngle) * rText;
    ctx.translate(tx, ty);

    // Rotate so numbers are upright when viewed from player camera
    ctx.rotate(midAngle - Math.PI / 2);

    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    const isDoubleZero = num === "00";
    ctx.font = isDoubleZero
      ? "700 84px 'Oswald', sans-serif"
      : "700 96px 'Oswald', sans-serif";

    ctx.fillStyle = "#ffffff";
    ctx.fillText(String(num), 0, 0);

    ctx.restore();
  }

  // 2. Concentric Metallic Gold Rings: Outer, Middle Dividing Ring, and Inner Base
  const drawRing = (radius, width, mainColor = "#ffd452", highlightColor = "#fff3c4") => {
    // Soft outer shadow/dark gold
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, TAU);
    ctx.strokeStyle = "#996d13";
    ctx.lineWidth = width + 2;
    ctx.stroke();

    // Main vibrant gold
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, TAU);
    ctx.strokeStyle = mainColor;
    ctx.lineWidth = width;
    ctx.stroke();

    // Specular highlight
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, TAU);
    ctx.strokeStyle = highlightColor;
    ctx.lineWidth = Math.max(1.5, width * 0.35);
    ctx.stroke();
  };

  const rMiddle = 865;

  drawRing(rOuter - 3, 5, "#ffd452", "#fff3c4");
  drawRing(rMiddle, 8, "#ffd452", "#fff3c4");
  drawRing(rInner + 2, 6, "#ffd452", "#fff3c4");

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 16;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = true;
  return texture;
}

// Generate burnished copper/mahogany texture for the central cone matching reference image
function createBurnishedConeTexture() {
  const canvas = document.createElement("canvas");
  canvas.width = 1024;
  canvas.height = 1024;
  const ctx = canvas.getContext("2d");

  const cx = 512;
  const cy = 512;
  const r = 512;

  // Rich copper-mahogany gradient
  const grad = ctx.createRadialGradient(cx, cy, 10, cx, cy, r);
  grad.addColorStop(0, "#8a4020");
  grad.addColorStop(0.35, "#5c2813");
  grad.addColorStop(0.70, "#381507");
  grad.addColorStop(1, "#1c0903");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, 1024, 1024);

  // 16 radial panel segments with soft specular shine
  const segments = 16;
  for (let i = 0; i < segments; i++) {
    const a1 = (i / segments) * TAU;
    const a2 = ((i + 1) / segments) * TAU;

    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.arc(cx, cy, r, a1, a2);
    ctx.closePath();

    ctx.fillStyle = i % 2 === 0 ? "rgba(255, 200, 140, 0.05)" : "rgba(0, 0, 0, 0.12)";
    ctx.fill();

    // Subtle seam line
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(cx + Math.cos(a1) * r, cy + Math.sin(a1) * r);
    ctx.strokeStyle = "rgba(0, 0, 0, 0.25)";
    ctx.lineWidth = 1.5;
    ctx.stroke();
  }

  // Specular sheen ring
  ctx.beginPath();
  ctx.arc(cx, cy, 260, 0, TAU);
  ctx.strokeStyle = "rgba(255, 255, 255, 0.15)";
  ctx.lineWidth = 40;
  ctx.stroke();

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

// Generate polished mahogany texture for outer wooden basin
function createMahoganyBowlTexture() {
  const canvas = document.createElement("canvas");
  canvas.width = 512;
  canvas.height = 512;
  const ctx = canvas.getContext("2d");

  const cx = 256;
  const cy = 256;
  const r = 256;

  const grad = ctx.createRadialGradient(cx, cy, 50, cx, cy, r);
  grad.addColorStop(0, "#d6a33c");
  grad.addColorStop(0.6, "#805016");
  grad.addColorStop(1, "#3a2006");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, 512, 512);

  // Concentric wood grain rings
  for (let rad = 20; rad < r; rad += 6) {
    ctx.beginPath();
    ctx.arc(cx, cy, rad, 0, TAU);
    ctx.strokeStyle = "rgba(0, 0, 0, 0.04)";
    ctx.lineWidth = 1.5;
    ctx.stroke();
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

// Cabinet & Outer Wooden Basin Component (Restored authentic 3D casino wheel)
function Cabinet() {
  const bowlTexture = useMemo(() => createMahoganyBowlTexture(), []);

  useEffect(() => {
    return () => bowlTexture.dispose();
  }, [bowlTexture]);

  const mahoganyMaterial = useMemo(() => {
    return new THREE.MeshStandardMaterial({
      map: bowlTexture,
      roughness: 0.20,
      metalness: 0.85,
    });
  }, [bowlTexture]);

  const brassMaterial = useMemo(() => {
    return new THREE.MeshStandardMaterial({
      color: "#ffc107", // True Gold Color
      metalness: 1.0,   // Fully metallic
      roughness: 0.15,  // Very smooth and reflective
    });
  }, []);

  const darkWallMaterial = useMemo(() => {
    return new THREE.MeshStandardMaterial({
      color: "#000000",
      roughness: 0.155,
      metalness: 0.25,
    });
  }, []);

  const greenFeltMaterial = useMemo(() => {
    return new THREE.MeshStandardMaterial({
      color: "#073d23",
      roughness: 0.94,
      metalness: 0.02,
    });
  }, []);



  return (
    <group>
      {/* Ground Drop Shadow on Green Felt - Soft grounding silhouette */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.28, 0]}>
        <circleGeometry args={[4.95, 64]} />
        <meshBasicMaterial color="#000000" transparent opacity={0.46} />
      </mesh>

      {/* Outer Polished Mahogany Bowl Casing (Rich wooden apron creating authentic front profile) */}
      <mesh position={[0, 0.08, 0]} receiveShadow castShadow>
        <cylinderGeometry args={[4.46, 4.66, 0.70, 128, 1, false]} />
        <primitive object={mahoganyMaterial} attach="material" />
      </mesh>

      {/* Outer Mahogany Bullnose Base Trim */}
      <mesh position={[0, -0.20, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <torusGeometry args={[4.62, 0.14, 24, 128]} />
        <primitive object={mahoganyMaterial} attach="material" />
      </mesh>

      {/* Main Outer Polished Gold Cabinet Wall */}
      <mesh position={[0, 0.16, 0]} receiveShadow castShadow>
        <cylinderGeometry args={[4.30, 4.46, 0.68, 128, 1, false]} />
        <primitive object={brassMaterial} attach="material" />
      </mesh>

      {/* Stepped Lower Bullnose Brass Rim (Creates gleaming front curve facing player) */}
      <mesh position={[0, -0.12, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <torusGeometry args={[4.42, 0.12, 24, 128]} />
        <primitive object={brassMaterial} attach="material" />
      </mesh>

      {/* Heavy Upper Gold Rim Top */}
      <mesh position={[0, 0.52, 0]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow castShadow>
        <torusGeometry args={[4.22, 0.24, 24, 128]} />
        <primitive object={brassMaterial} attach="material" />
      </mesh>

      {/* Outer Brass Lip Rim framing full strips */}
      <mesh position={[0, 0.56, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <torusGeometry args={[3.92, 0.048, 16, 128]} />
        <primitive object={brassMaterial} attach="material" />
      </mesh>
    </group>
  );
}

// Rotating Number Rotor, Pocket Separators & Turned Gold Turret (Exact match to image_9330bb23.jpg)
function NumberRotor({ wheelRef, highlightNumber, sequence }) {
  const total = sequence.length;
  const step = TAU / total;

  const [fontLoaded, setFontLoaded] = useState(() => {
    return typeof document !== "undefined" && document.fonts && document.fonts.check
      ? document.fonts.check("700 96px 'Oswald'")
      : false;
  });

  useEffect(() => {
    let mounted = true;
    const loadFont = async () => {
      try {
        if (document.fonts) {
          await document.fonts.load("700 96px 'Oswald'");
          await document.fonts.load("700 84px 'Oswald'");
          await document.fonts.ready;
        }
      } catch (e) {}
      if (mounted) {
        setFontLoaded(true);
      }
    };
    loadFont();

    const t1 = setTimeout(() => {
      if (mounted) setFontLoaded(true);
    }, 250);
    const t2 = setTimeout(() => {
      if (mounted) setFontLoaded(true);
    }, 700);

    return () => {
      mounted = false;
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  // Single unified high-res rotor texture
  const rotorTexture = useMemo(() => {
    const tex = createPhotorealisticRotorTexture(sequence);
    tex.needsUpdate = true;
    return tex;
  }, [sequence, fontLoaded]);
  const coneTexture = useMemo(() => createBurnishedConeTexture(), []);

  useEffect(() => {
    return () => {
      rotorTexture.dispose();
      coneTexture.dispose();
    };
  }, [rotorTexture, coneTexture]);

  const brassMaterial = useMemo(() => {
    return new THREE.MeshStandardMaterial({
      color: "#ffc107",
      metalness: 1.0,
      roughness: 0.15,
    });
  }, []);

  const coneMaterial = useMemo(() => {
    return new THREE.MeshStandardMaterial({
      map: coneTexture,
      roughness: 0.18,
      metalness: 0.85,
    });
  }, [coneTexture]);

  return (
    <group ref={wheelRef}>
      {/* 1. Photorealistic Numbered Ring Surface */}
      <mesh position={[0, 0.585, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[1.86, 3.86, 128]} />
        <meshStandardMaterial
          key={fontLoaded ? "rotor-oswald-ready" : "rotor-oswald-init"}
          map={rotorTexture}
          roughness={0.85}
          metalness={0.02}
        />
      </mesh>

      {/* 2. Concentric 3D Gleaming Golden Dividing Ring under the edge numbers */}
      <mesh position={[0, 0.59, 0]} rotation={[-Math.PI / 2, 0, 0]} castShadow>
        <torusGeometry args={[3.26, 0.026, 16, 128]} />
        <primitive object={brassMaterial} attach="material" />
      </mesh>

      {/* 3. Central Burnished Mahogany/Copper Cone */}
      <mesh position={[0, 0.65, 0]} castShadow receiveShadow>
        <coneGeometry args={[1.85, 0.44, 64]} />
        <primitive object={coneMaterial} attach="material" />
      </mesh>

      {/* 3. Central Faceted Diamond Turret with 8 Radial Gold Spokes (Exact match to reference photo) */}
      <FacetedDiamondTurret material={brassMaterial} />
    </group>
  );
}

// Precision Faceted Diamond Turret with 4 Prominent Tapered Handles & Brilliant Gem (Exact match to reference image)
function FacetedDiamondTurret({ material }) {
  // Stepped lathe base hub
  const hubGeometry = useMemo(() => {
    const pts = [];
    pts.push(new THREE.Vector2(0.68, 0.00));
    pts.push(new THREE.Vector2(0.66, 0.05));
    pts.push(new THREE.Vector2(0.58, 0.09));
    pts.push(new THREE.Vector2(0.56, 0.15));
    pts.push(new THREE.Vector2(0.58, 0.20));
    pts.push(new THREE.Vector2(0.54, 0.25));
    pts.push(new THREE.Vector2(0.50, 0.32));
    pts.push(new THREE.Vector2(0.46, 0.35));
    const geom = new THREE.LatheGeometry(pts, 48);
    geom.computeVertexNormals();
    return geom;
  }, []);

  // Sparkling Diamond Physical Material with refractive brilliance
  const diamondMaterial = useMemo(() => {
    return new THREE.MeshPhysicalMaterial({
      color: "#ffffff",
      emissive: "#0284c7",
      emissiveIntensity: 0.22,
      roughness: 0.02,
      metalness: 0.05,
      transmission: 0.92,
      ior: 2.417,
      thickness: 1.5,
      specularIntensity: 1.0,
      specularColor: "#ffffff",
      flatShading: true,
      transparent: true,
      opacity: 0.95,
    });
  }, []);

  return (
    <group position={[0, 0.72, 0]}>
      {/* 1. Turned Gold Center Hub Pedestal */}
      <mesh geometry={hubGeometry} castShadow receiveShadow>
        <primitive object={material} attach="material" />
      </mesh>

      {/* 2. Concentric Gold Ring connecting the spokes halfway down the cone (exact match to image) */}
      <mesh position={[0, -0.06, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.92, 0.024, 16, 64]} />
        <primitive object={material} attach="material" />
      </mesh>

      {/* 3. 8 Radial Golden Spokes with gold ball tips (exact match to reference image) */}
      {[
        0,
        Math.PI / 4,
        Math.PI / 2,
        (3 * Math.PI) / 4,
        Math.PI,
        (5 * Math.PI) / 4,
        (3 * Math.PI) / 2,
        (7 * Math.PI) / 4
      ].map((angle, i) => (
        <group key={`spoke-${i}`} rotation={[0, angle, 0]}>
          {/* Main radial gold bar sloping down the cone */}
          <mesh position={[1.18, -0.06, 0]} rotation={[0, 0, -0.20]} castShadow>
            <boxGeometry args={[1.28, 0.038, 0.048]} />
            <primitive object={material} attach="material" />
          </mesh>
          {/* Spherical gold ball tip meeting the pocket inner ring */}
          <mesh position={[1.82, -0.19, 0]} castShadow>
            <sphereGeometry args={[0.065, 16, 16]} />
            <primitive object={material} attach="material" />
          </mesh>
        </group>
      ))}

      {/* 4. 8 Ornate Gold Prongs / Claws holding the diamond base */}
      {[
        0,
        Math.PI / 4,
        Math.PI / 2,
        (3 * Math.PI) / 4,
        Math.PI,
        (5 * Math.PI) / 4,
        (3 * Math.PI) / 2,
        (7 * Math.PI) / 4
      ].map((angle, i) => (
        <group key={`claw-${i}`} rotation={[0, angle, 0]}>
          <mesh position={[0.50, 0.30, 0]} rotation={[0, 0, -0.16]} castShadow>
            <cylinderGeometry args={[0.024, 0.034, 0.24, 12]} />
            <primitive object={material} attach="material" />
          </mesh>
          <mesh position={[0.52, 0.42, 0]} castShadow>
            <sphereGeometry args={[0.038, 12, 12]} />
            <primitive object={material} attach="material" />
          </mesh>
        </group>
      ))}

      {/* 5. Giant Multi-Faceted Brilliant Cut Diamond (Resting centrally in the gold hub) */}
      <group position={[0, 0.44, 0]}>
        {/* Diamond Crown (Upper facet tiers) */}
        <mesh position={[0, 0.12, 0]} castShadow>
          <cylinderGeometry args={[0.34, 0.54, 0.24, 16, 1]} />
          <primitive object={diamondMaterial} attach="material" />
        </mesh>

        {/* Diamond Table (Top flat octagonal cap) */}
        <mesh position={[0, 0.24, 0]} rotation={[-Math.PI / 2, 0, 0]} castShadow>
          <circleGeometry args={[0.34, 16]} />
          <primitive object={diamondMaterial} attach="material" />
        </mesh>

        {/* Diamond Pavilion (Lower tapering faceted cone seated into hub) */}
        <mesh position={[0, -0.16, 0]} rotation={[Math.PI, 0, 0]} castShadow>
          <coneGeometry args={[0.54, 0.34, 16]} />
          <primitive object={diamondMaterial} attach="material" />
        </mesh>

        {/* Girdle Accent Ring */}
        <mesh position={[0, 0.0, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <torusGeometry args={[0.54, 0.022, 12, 32]} />
          <primitive object={material} attach="material" />
        </mesh>

        {/* Inner Sparkling Cyan Crystal Core */}
        <mesh position={[0, 0.04, 0]}>
          <octahedronGeometry args={[0.26, 0]} />
          <meshStandardMaterial
            color="#ffffff"
            emissive="#38bdf8"
            emissiveIntensity={0.85}
            roughness={0.0}
          />
        </mesh>

        {/* Inner Diamond Sparkle Point Light */}
        <pointLight position={[0, 0.10, 0]} intensity={1.8} distance={3.5} color="#bae6fd" />
      </group>
    </group>
  );
}

// Complete Casino Roulette Spin Physics & Synchronized Settlement Engine
function Scene({ isSpinning, winningNumber, sequence, spinDuration = 4.0, wheelZoom = false, onSettled }) {
  const total = sequence.length;
  const step = TAU / total;

  const wheelRef = useRef(null);
  const ballPivotRef = useRef(null);
  const ballRef = useRef(null);

  const stateRef = useRef("STOPPED");
  const wheelAngle = useRef(0);
  const ballAngle = useRef(0);

  const wheelSpeed = useRef(2.4);
  const ballSpeed = useRef(-7.6);

  const ballRadius = useRef(2.54);
  const ballHeight = useRef(0.725);

  const settleStartTime = useRef(0);
  const settleDuration = useRef(spinDuration);
  const settleStartWheel = useRef(0);
  const settleStartBall = useRef(0);
  const settleTargetWheel = useRef(0);
  const settleTargetBall = useRef(0);
  const lockedWinningNumber = useRef(null);

  const [highlightedNum, setHighlightedNum] = useState(null);

  const isSpinningPrev = useRef(false);
  const settleTimerRef = useRef(null);
  const initializedRef = useRef(false);

  const parkBallAtNumber = useCallback((num) => {
    if (num === null || num === undefined) return;
    const targetIdx = sequence.findIndex(
      (val) => String(val) === String(num)
    );
    const validIdx = targetIdx >= 0 ? targetIdx : 0;
    const pocketTargetAngle = validIdx * step;

    ballAngle.current = wheelAngle.current - pocketTargetAngle;
    ballRadius.current = 2.54;
    ballHeight.current = 0.725;
    stateRef.current = "STOPPED";
    setHighlightedNum(num);
  }, [sequence, step]);

  const startSettling = useCallback((targetNum, dur = 3.4) => {
    stateRef.current = "SETTLING";
    lockedWinningNumber.current = targetNum;

    const targetIdx = sequence.findIndex(
      (val) => String(val) === String(targetNum)
    );
    const validIdx = targetIdx >= 0 ? targetIdx : 0;
    const pocketTargetAngle = validIdx * step;

    settleDuration.current = dur;
    settleStartTime.current = performance.now() / 1000;

    settleStartWheel.current = wheelAngle.current;
    settleStartBall.current = ballAngle.current;

    const totalWheelRot = Math.max(1.8, wheelSpeed.current) * (dur * 0.42);
    const finalWheel = settleStartWheel.current + totalWheelRot;
    settleTargetWheel.current = finalWheel;

    const baseBallTarget = finalWheel - pocketTargetAngle;
    let finalBall = settleStartBall.current;
    while (finalBall > baseBallTarget - Math.PI * 6) {
      finalBall -= TAU;
    }
    finalBall += (baseBallTarget - finalBall) % TAU;
    if (finalBall > settleStartBall.current - Math.PI * 4) {
      finalBall -= TAU;
    }
    settleTargetBall.current = finalBall;
  }, [sequence, step]);

  useEffect(() => {
    // When NOT spinning: wheel rests completely.
    if (!isSpinning) {
      if (settleTimerRef.current) {
        clearTimeout(settleTimerRef.current);
        settleTimerRef.current = null;
      }
      if (winningNumber !== null && winningNumber !== undefined) {
        parkBallAtNumber(winningNumber);
      }
      isSpinningPrev.current = false;
      return;
    }

    // When isSpinning becomes TRUE: trigger the spin!
    if (!isSpinningPrev.current) {
      isSpinningPrev.current = true;
      stateRef.current = "FREE_SPIN";
      setHighlightedNum(null);
      wheelSpeed.current = 2.6;
      ballSpeed.current = -8.2;
      ballRadius.current = 2.74;
      ballHeight.current = 0.725;

      const freeSpinTime = 0.6;
      const settleDur = Math.max(2.0, (spinDuration || 4.0) - freeSpinTime);

      if (settleTimerRef.current) {
        clearTimeout(settleTimerRef.current);
      }
      settleTimerRef.current = setTimeout(() => {
        const target = winningNumber !== null && winningNumber !== undefined ? winningNumber : 0;
        startSettling(target, settleDur);
      }, freeSpinTime * 1000);
    }
  }, [isSpinning, winningNumber, spinDuration, parkBallAtNumber, startSettling]);

  useFrame(({ camera }, delta) => {
    const now = performance.now() / 1000;

    // Smooth camera zoom: angled 3D perspective showing proper front side of the wheel in full view
    const targetY = wheelZoom ? 4.0 : 5.4;
    const targetZ = wheelZoom ? 6.2 : 9.0;
    const targetLookAtY = wheelZoom ? 0.10 : -0.30;
    camera.position.y += (targetY - camera.position.y) * Math.min(1, delta * 3.5);
    camera.position.z += (targetZ - camera.position.z) * Math.min(1, delta * 3.5);
    camera.lookAt(0, targetLookAtY, 0);

    if (stateRef.current === "IDLE" || stateRef.current === "STOPPED") {
      // The wheel rests completely when idle/stopped. No rotation at all.
    } else if (stateRef.current === "FREE_SPIN") {
      wheelAngle.current += wheelSpeed.current * delta;
      ballAngle.current += ballSpeed.current * delta;
      ballRadius.current = 3.65;
      ballHeight.current = 0.725;
      ballSpeed.current = THREE.MathUtils.lerp(ballSpeed.current, -6.8, delta * 0.5);
    } else if (stateRef.current === "SETTLING") {
      const elapsed = now - settleStartTime.current;
      const progress = Math.min(1, elapsed / settleDuration.current);

      const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
      const pEase = easeOutCubic(progress);

      wheelAngle.current = THREE.MathUtils.lerp(
        settleStartWheel.current,
        settleTargetWheel.current,
        pEase
      );

      ballAngle.current = THREE.MathUtils.lerp(
        settleStartBall.current,
        settleTargetBall.current,
        pEase
      );

      if (progress < 0.45) {
        const q = progress / 0.45;
        ballRadius.current = THREE.MathUtils.lerp(3.65, 2.70, q);
        ballHeight.current = 0.725;
      } else if (progress < 0.80) {
        const q = (progress - 0.45) / 0.35;
        const bounceOffset = Math.abs(Math.sin(q * Math.PI * 6)) * 0.045 * (1 - q);
        const radialWobble = Math.cos(q * Math.PI * 4) * 0.015 * (1 - q);

        ballRadius.current = THREE.MathUtils.lerp(2.70, 2.54, q) + radialWobble;
        ballHeight.current = 0.725 + bounceOffset;
      } else {
        const q = (progress - 0.80) / 0.20;
        const microBounce = Math.abs(Math.sin(q * Math.PI * 3)) * 0.012 * Math.pow(1 - q, 2);

        ballRadius.current = 2.54;
        ballHeight.current = 0.725 + microBounce;
      }

      if (progress >= 1 && stateRef.current !== "STOPPED") {
        stateRef.current = "STOPPED";

        const landed = lockedWinningNumber.current !== null
          ? lockedWinningNumber.current
          : winningNumber;

        setHighlightedNum(landed);
        onSettled?.(landed);
      }
    } else if (stateRef.current === "STOPPED") {
      // Static at rest.
    }

    if (wheelRef.current) {
      wheelRef.current.rotation.y = wheelAngle.current;
    }
    if (ballPivotRef.current) {
      ballPivotRef.current.rotation.y = ballAngle.current;
    }
    if (ballRef.current) {
      ballRef.current.position.set(ballRadius.current, ballHeight.current, 0);
    }
  });

  return (
    <>
      {/* Ultra-realistic HDR Environment for stunning glossy reflections */}
      <Suspense fallback={null}>
        <Environment preset="city" />
      </Suspense>

      {/* Boosted ambient and key lighting to highlight gold brass rims and diamond facets */}
      <ambientLight intensity={1.1} />
      <directionalLight position={[0, 7, 7]} intensity={2.4} color="#fff6e8" castShadow />
      <pointLight position={[0, 2.2, 5.5]} intensity={1.8} color="#ffe8b8" />
      <pointLight position={[-6, 4, 2]} intensity={1.2} color="#ffd8a8" />
      <pointLight position={[6, 4, 2]} intensity={1.2} color="#ffd8a8" />

      <Cabinet />
      <NumberRotor
        wheelRef={wheelRef}
        highlightNumber={highlightedNum}
        sequence={sequence}
      />

      {/* Glossy Pure Ivory White Roulette Ball */}
      <group ref={ballPivotRef}>
        <mesh ref={ballRef} castShadow receiveShadow>
          <sphereGeometry args={[0.135, 32, 32]} />
          <meshPhysicalMaterial
            color="#ffffff"
            roughness={0.02}
            metalness={0.0}
            clearcoat={1.0}
            clearcoatRoughness={0.01}
            reflectivity={1.0}
          />
        </mesh>
      </group>
    </>
  );
}

// Main Exported Interactive 3D Roulette Component for FunRoulette arcade cabinet
export default function FunRouletteWheel3D({
  isSpinning = false,
  winningNumber = null,
  spinDuration = 4.0,
  variant = "EUROPEAN",
  wheelZoom = false,
  onSettled,
}) {
  const [currentVariant, setCurrentVariant] = useState(variant);
  const [internalSpinning, setInternalSpinning] = useState(false);
  const [internalTarget, setInternalTarget] = useState(null);
  const [internalResult, setInternalResult] = useState(null);
  const containerRef = useRef(null);

  const isControlledByGame = typeof isSpinning === "boolean" && onSettled !== undefined;

  const sequence = useMemo(() => {
    return currentVariant === "AMERICAN" ? AMERICAN_WHEEL : EUROPEAN_WHEEL;
  }, [currentVariant]);

  const activeSpinning = isSpinning || internalSpinning;
  const activeWinningNumber = winningNumber !== null ? winningNumber : internalTarget;

  // Play MP3 audio file when wheel starts spinning, smoothly stop when settled
  useEffect(() => {
    if (activeSpinning) {
      sounds.playWheelSpinAudio();
    } else {
      sounds.stopWheelSpinAudio();
    }
    return () => {
      sounds.stopWheelSpinAudio();
    };
  }, [activeSpinning]);

  const handleSettled = useCallback(
    (num) => {
      setInternalResult(num);
      setInternalSpinning(false);
      sounds.stopWheelSpinAudio();
      onSettled?.(num);
    },
    [onSettled]
  );

  const handleStandaloneSpin = () => {
    if (activeSpinning) return;
    setInternalResult(null);
    setInternalSpinning(true);
    setInternalTarget(null);

    setTimeout(() => {
      const pick = sequence[Math.floor(Math.random() * sequence.length)];
      setInternalTarget(pick);
    }, 2200);
  };

  const getResultColorBadge = (num) => {
    if (num === null || num === undefined) return null;
    if (num === 0 || num === "00") return { text: "GREEN", bg: "#008544" };
    const n = Number(num);
    if (RED_NUMBERS.has(n)) return { text: "RED", bg: "#b8121f" };
    return { text: "BLACK", bg: "#131518" };
  };

  const badge = getResultColorBadge(internalResult);

  return (
    <div
      ref={containerRef}
      className="two-ring-roulette-shell"
      style={{ width: "100%", height: "100%", position: "relative", overflow: "visible" }}
    >
      {/* Vignette removed for clean overlap */}

      {/* 3D WebGL Canvas matching image_9330bb23.jpg */}
      <div
        className="two-ring-roulette-canvas"
        style={{
          position: "absolute",
          top: "0",
          bottom: "0",
          left: "0",
          right: "0",
          width: "100%",
          height: "100%",
        }}
      >
        <Canvas
          shadows
          dpr={[1, 1.75]}
          camera={{
            position: [0, 5.4, 9.0],
            fov: 46,
            near: 0.1,
            far: 60,
          }}
          gl={{
            antialias: true,
            alpha: true,
            powerPreference: "high-performance",
          }}
          onCreated={({ camera, gl }) => {
            camera.lookAt(0, -0.30, 0);
            gl.outputColorSpace = THREE.SRGBColorSpace;
            gl.toneMapping = THREE.ACESFilmicToneMapping;
            gl.toneMappingExposure = 0.90;
          }}
        >
          <Scene
            isSpinning={activeSpinning}
            winningNumber={activeWinningNumber}
            spinDuration={spinDuration}
            sequence={sequence}
            wheelZoom={wheelZoom}
            onSettled={handleSettled}
          />
        </Canvas>
      </div>

      {/* Standalone Control Overlay */}
      {!isControlledByGame && (
        <div
          style={{
            position: "absolute",
            bottom: "8px",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 10,
            display: "flex",
            alignItems: "center",
            gap: "10px",
            pointerEvents: "auto",
          }}
        >
          <div
            style={{
              background: "rgba(10, 15, 12, 0.85)",
              backdropFilter: "blur(8px)",
              border: "1px solid rgba(226, 182, 71, 0.4)",
              borderRadius: "20px",
              padding: "3px",
              display: "flex",
              gap: "4px",
            }}
          >
            <button
              onClick={() => !activeSpinning && setCurrentVariant("EUROPEAN")}
              disabled={activeSpinning}
              style={{
                padding: "4px 12px",
                borderRadius: "16px",
                fontSize: "11px",
                fontWeight: "700",
                border: "none",
                cursor: activeSpinning ? "not-allowed" : "pointer",
                background: currentVariant === "EUROPEAN"
                  ? "linear-gradient(180deg, #e2b647 0%, #aa820a 100%)"
                  : "transparent",
                color: currentVariant === "EUROPEAN" ? "#ffffff" : "#aaaaaa",
                transition: "all 0.2s ease",
              }}
            >
              🇪🇺 Single 0
            </button>
            <button
              onClick={() => !activeSpinning && setCurrentVariant("AMERICAN")}
              disabled={activeSpinning}
              style={{
                padding: "4px 12px",
                borderRadius: "16px",
                fontSize: "11px",
                fontWeight: "700",
                border: "none",
                cursor: activeSpinning ? "not-allowed" : "pointer",
                background: currentVariant === "AMERICAN"
                  ? "linear-gradient(180deg, #e2b647 0%, #aa820a 100%)"
                  : "transparent",
                color: currentVariant === "AMERICAN" ? "#ffffff" : "#aaaaaa",
                transition: "all 0.2s ease",
              }}
            >
              🇺🇸 Double 00
            </button>
          </div>

          <button
            onClick={handleStandaloneSpin}
            disabled={activeSpinning}
            style={{
              padding: "6px 24px",
              fontSize: "14px",
              fontWeight: "800",
              letterSpacing: "1px",
              color: "#ffffff",
              background: activeSpinning
                ? "linear-gradient(180deg, #555555 0%, #333333 100%)"
                : "linear-gradient(180deg, #e2b647 0%, #aa820a 100%)",
              border: "1.5px solid #ffe89e",
              borderRadius: "24px",
              boxShadow: activeSpinning
                ? "none"
                : "0 4px 14px rgba(226, 182, 71, 0.4)",
              cursor: activeSpinning ? "not-allowed" : "pointer",
              transition: "all 0.2s ease",
              textTransform: "uppercase",
            }}
          >
            {activeSpinning ? "Spinning..." : "SPIN"}
          </button>

          {internalResult !== null && !activeSpinning && (
            <div
              style={{
                background: "rgba(10, 15, 12, 0.90)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(226, 182, 71, 0.6)",
                borderRadius: "20px",
                padding: "4px 14px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                color: "#ffffff",
              }}
            >
              <span style={{ fontSize: "11px", color: "#e2b647", fontWeight: "700", fontFamily: "'Oswald', sans-serif" }}>
                Winning Number: <strong style={{ fontFamily: "'Oswald', sans-serif", fontSize: "13px" }}>{internalResult}</strong>
              </span>
              {badge && (
                <span
                  style={{
                    background: badge.bg,
                    color: "#ffffff",
                    padding: "2px 8px",
                    borderRadius: "4px",
                    fontSize: "10px",
                    fontWeight: "800",
                  }}
                >
                  {badge.text}
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}











